import { FreeSpinsInfoEvent, SpinResponse } from '@cgs/data-access';
import { Container, EventDispatcher, EventStream } from '@cgs/wge';
import {
  AbstractListener,
  GameStateMachine,
  GameStateMachineNotifierComponent,
  GameStateMachineStates,
  ISlotPopupCoordinator,
  T_GameStateMachineNotifierComponent,
  T_ISlotPopupCoordinator,
} from '@cgs/machines';

export const CGS_HUD_COORDINATOR_TOKEN = Symbol('HUD_COORDINATOR_TOKEN');

export class CGSHudCoordinator implements AbstractListener {
  private _interactiveStates: string[] = [
    GameStateMachineStates.Idle,
    GameStateMachineStates.RegularSpin,
  ];
  private readonly _gameStateMachine: GameStateMachine<SpinResponse>;
  private readonly _slotPopupCoordinator: ISlotPopupCoordinator;

  constructor(container: Container) {
    const gsmComponent = container.resolve<GameStateMachineNotifierComponent>(
      T_GameStateMachineNotifierComponent
    );
    this._gameStateMachine = gsmComponent.gameStateMachine;
    gsmComponent.notifier.addListener(this);

    this._slotPopupCoordinator = container.resolve<ISlotPopupCoordinator>(T_ISlotPopupCoordinator);
    this._slotPopupCoordinator.popupShown.listen(() => this._onPopupShown());
    this._slotPopupCoordinator.popupHidden.listen(() => this._onPopupHidden());
  }

  raiseUnavailable(): void {
    this._unavailableDispatcher.dispatchEvent();
  }

  private _onPopupShown(): void {
    this._unavailableDispatcher.dispatchEvent();
  }

  private _onPopupHidden(): void {
    if (this.isAvailable) {
      this._availableDispatcher.dispatchEvent();
    }
  }

  public enableHud(): void {
    if (this.isAvailable) {
      this._hudEnableDispatcher.dispatchEvent();
    }
  }

  public disableHud(): void {
    this._hudDisableDispatcher.dispatchEvent();
  }

  public onStateEntered(slotState: string): void {
    switch (slotState) {
      case GameStateMachineStates.Bonus:
        this._unavailableDispatcher.dispatchEvent();
        break;
      case GameStateMachineStates.FreeSpinRecovery:
        this._unavailableDispatcher.dispatchEvent();
        break;
      case GameStateMachineStates.FreeSpin:
        this._unavailableDispatcher.dispatchEvent();
        break;
      case GameStateMachineStates.Scatter:
        this._unavailableDispatcher.dispatchEvent();
        break;
      case GameStateMachineStates.RegularSpin:
      case GameStateMachineStates.Idle:
        if (!this._slotPopupCoordinator.isPopupShown()) {
          this._availableDispatcher.dispatchEvent();
        }
        break;
    }
  }

  public onStateExited(slotState: string): void {
    switch (slotState) {
      case GameStateMachineStates.RegularSpin:
      case GameStateMachineStates.Idle:
        this._unavailableDispatcher.dispatchEvent();
        break;
      case GameStateMachineStates.Bonus:
        this._availableDispatcher.dispatchEvent();
        break;
    }
  }

  get isAvailable(): boolean {
    return (
      !this._slotPopupCoordinator.isPopupShown() &&
      this._interactiveStates.some((stateId) => this._gameStateMachine.isActive(stateId))
    );
  }

  get canChangeBet(): boolean {
    return (
      this._gameStateMachine.curResponse &&
      (!this._gameStateMachine.curResponse.freeSpinsInfo ||
        this._gameStateMachine.curResponse.freeSpinsInfo.event ===
          FreeSpinsInfoEvent.FreeSpinsFinished ||
        this._gameStateMachine.curResponse.freeSpinsInfo.event ===
          FreeSpinsInfoEvent.FreeSpinsStarted)
    );
  }

  get updateTotalWinAfterScatter(): boolean {
    return false;
  }

  get updateTotalWinOnRegularSpins(): boolean {
    return true;
  }

  private _availableDispatcher: EventDispatcher<void> = new EventDispatcher<void>();
  get available(): EventStream<void> {
    return this._availableDispatcher.eventStream;
  }

  private _unavailableDispatcher: EventDispatcher<void> = new EventDispatcher<void>();
  get unavailable(): EventStream<void> {
    return this._unavailableDispatcher.eventStream;
  }

  private _enabledDispatcher: EventDispatcher<void> = new EventDispatcher<void>();
  get enabled(): EventStream<void> {
    return this._enabledDispatcher.eventStream;
  }

  private _disabledDispatcher: EventDispatcher<void> = new EventDispatcher<void>();
  get disabled(): EventStream<void> {
    return this._disabledDispatcher.eventStream;
  }

  private _hudEnableDispatcher: EventDispatcher<void> = new EventDispatcher<void>();
  get hudEnable(): EventStream<void> {
    return this._hudEnableDispatcher.eventStream;
  }

  private _hudDisableDispatcher: EventDispatcher<void> = new EventDispatcher<void>();
  get hudDisable(): EventStream<void> {
    return this._hudDisableDispatcher.eventStream;
  }
}
